import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import ShareModal from "../Modals/ShareModal";
import { IoCheckmarkCircle } from "react-icons/io5";
import { Bell, Menu } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { MdKeyboardArrowDown } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { useNotificationUnreadCount } from "../../context/SocketContext";
import { AccountProfile, clearAccountProfile } from "../../redux/ApiSlice/AccountProfileSlice";
import { getSwitchAccountList } from "../../redux/ApiSlice/SwitchAccountSlice";

const AccountProfileSection = React.memo(({ setActiveTab, setShow, show, setShowProfileModal }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [share, setShare] = useState(false);
    const [switchTab, setSwitchTab] = useState(false);
    const [isAboutExpanded, setIsAboutExpanded] = useState(false);
    const portalTarget = typeof document !== "undefined"
        ? document.getElementById("mobile-layout-root") || document.body
        : null;

    const ProfileDetails = useSelector((state) => state.AccountProfile);
    const { SwitchAccountList } = useSelector((state) => state.switchAccount);
    const notificationUnreadCountWs = useNotificationUnreadCount();
    const notificationUnreadCount = notificationUnreadCountWs?.data || { unread_count: 0 };

    const {
        User_profile,
        following_count,
        followers_count,
        posts_count
    } = ProfileDetails;
    const activeAccessToken = typeof window !== "undefined" ? localStorage.getItem("accessToken") : "";

    const handleOpenShareModal = () => {
        setShare(!share);
    };


    let originalURL = User_profile.avatar;

    const getListOfSwitchAccount = async () => {
        if (!switchTab) {
            dispatch(getSwitchAccountList());
        }
        setSwitchTab(!switchTab);
    }

    const handleSwitchAccount = async (item) => {
        try {
            // const response = await dispatch(switchAccount({ session_key: item }));
            if (item) {
                localStorage.setItem("accessToken", item);
                dispatch(clearAccountProfile());
                dispatch(AccountProfile());
                navigate("/account")
            }
            setSwitchTab(!switchTab);
        } catch (error) {
            console.error("Error switching account:", error);
        }
    };

    const handleOpenSwitchModal = () => {
        getListOfSwitchAccount()
    };

    const isCurrentActiveAccount = (item) => {
        if (!item) return false;
        const currentUsername = `${User_profile?.username || ""}`.toLowerCase();
        const sessionUsername = `${item?.username || item?.user?.username || ""}`.toLowerCase();
        return item?.access_token === activeAccessToken || (currentUsername && sessionUsername && currentUsername === sessionUsername);
    };


    // Function to truncate text to specific word count
    const truncateText = (text, wordLimit) => {
        if (!text) return '';
        const words = text.split(' ');
        if (words.length <= wordLimit) return text;
        return words.slice(0, wordLimit).join(' ') + '...';
    };

    // Function to toggle about us expansion
    const toggleAboutExpansion = () => {
        setIsAboutExpanded(!isAboutExpanded);
    };

    return (
        <div className={show ? "overflow-hidden" : ""}>
            <div className="grid grid-cols-12 px-2 mt-2">
                <div className="col-span-6 flex items-center gap-x-1 relative">
                    <p className="text-[#000000] dark:text-white font-bold text-[12px] flex items-center gap-x-1">
                        <span>
                            {(() => {
                                const fullName = User_profile.username || "";
                                if (!fullName) return <span className="inline-block h-3 w-20 rounded bg-gray-300 dark:bg-gray-600 animate-pulse" />;
                                return fullName.length <= 12 ? fullName : fullName.substring(0, 12) + '...';
                            })()}
                        </span>
                        <span>
                            <MdKeyboardArrowDown
                                onClick={handleOpenSwitchModal}
                                className={`cursor-pointer text-[1.3rem] ml-[3px] dark:text-white ${switchTab && "rotate-180"}`}
                            />
                        </span>
                    </p>
                </div>
                <div className="col-span-6 flex justify-end items-center gap-x-3">
                    <div
                        onClick={() => navigate("/notification")}
                        className="relative inline-flex items-center cursor-pointer"
                    >
                        <Bell className="h-4 w-4 text-black dark:text-white" />
                        {notificationUnreadCount.unread_count > 0 && (
                            <span className="absolute rounded-full text-[8px] font-medium content-[''] leading-none grid place-items-center top-[4%] right-[2%] translate-x-2/4 -translate-y-2/4 bg-red-500 text-white min-w-[14px] min-h-[14px]">
                                {notificationUnreadCount.unread_count}
                            </span>
                        )}
                    </div>
                    <Menu
                        onClick={() => navigate("/account/settings")}
                        className="h-5 w-5 stroke-2 text-black dark:text-white cursor-pointer"
                    />
                </div>
            </div>

            <div className="px-2 mt-2">
                <div className="flex items-center gap-3">
                    <div className="shrink-0">
                        <img
                            onClick={() => setShowProfileModal(true)}
                            src={User_profile.avatar}
                            loading="lazy"
                            alt={User_profile.full_name}
                            onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
                            className="h-[4rem] w-[4rem] rounded-full object-cover border border-gray-400 dark:border-gray-800"
                        />
                        <div
                            onClick={() => setShowProfileModal(true)}
                            style={{ display: "none" }}
                            className="h-[4rem] w-[4rem] rounded-full border border-gray-400 dark:border-gray-600 bg-gray-200 dark:bg-gray-700 items-center justify-center cursor-pointer"
                        >
                            <span className="text-[2rem] text-gray-400 dark:text-gray-500 select-none">
                                {(User_profile.username || "?")[0].toUpperCase()}
                            </span>
                        </div>
                    </div>
                    <div className="flex flex-col gap-1 pt-1 min-w-0">
                        <p className="text-black dark:text-white font-bold text-[15px] line-clamp-1">
                            {User_profile.full_name}
                        </p>
                        <div className="flex items-center gap-3 text-[10px] font-medium text-black dark:text-white">
                            <span onClick={() => navigate("/account")} className="cursor-pointer">
                                <span className="font-bold">{posts_count}</span> Posts
                            </span>
                            <span onClick={() => navigate("/accounts/followers")} className="cursor-pointer">
                                <span className="font-bold">{followers_count}</span> Followers
                            </span>
                            <span onClick={() => navigate("/accounts/following")} className="cursor-pointer">
                                <span className="font-bold">{following_count}</span> Following
                            </span>
                        </div>
                    </div>
                </div>

                {User_profile?.portfolio && (
                    <a
                        href={User_profile?.portfolio}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-black dark:text-white hover:text-blue-700 cursor-pointer text-[10px] leading-[14px] mt-2 line-clamp-1"
                    >
                        {User_profile?.portfolio}
                    </a>
                )}
                <p
                    onClick={toggleAboutExpansion}
                    className="text-[#000000] cursor-pointer dark:text-white font-medium text-[10px] leading-[15px] mt-0.5"
                >
                    {isAboutExpanded
                        ? User_profile.about_us
                        : truncateText(User_profile.about_us, 10)
                    }
                </p>

                <div className="grid grid-cols-2 gap-2 mt-2">
                    <button
                        onClick={() => navigate("/account/edit/profile")}
                        className="py-1.5 text-[12px] font-semibold bg-[#BDE6FF] dark:bg-[#333333] text-gray-900 dark:text-white rounded-md"
                    >
                        Edit Profile
                    </button>
                    <button
                        onClick={handleOpenShareModal}
                        className="py-1.5 text-[12px] font-semibold bg-[#BDE6FF] dark:bg-[#333333] text-gray-900 dark:text-white rounded-md"
                    >
                        Share Profile
                    </button>
                </div>
            </div>

            <ShareModal
                openBottom={share}
                closeDrawerBottom={handleOpenShareModal}
            />

            {/* Switch Account Modal * */}
            {portalTarget && createPortal(
                <AnimatePresence>
                    {switchTab && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute inset-0 z-[120] flex items-end bg-black/40"
                            onClick={() => setSwitchTab(false)}
                        >
                            <motion.div
                                animate={{ y: 0 }}
                                exit={{ y: "100%" }}
                                initial={{ y: "100%" }}
                                onClick={(e) => e.stopPropagation()}
                                transition={{ type: "spring", damping: 30, stiffness: 300 }}
                                className="w-full bg-white dark:bg-gray-900 rounded-t-2xl min-h-[10rem] max-h-[25rem] flex flex-col shadow-md"
                            >
                                <div className="flex flex-col justify-center items-center px-4 py-2">
                                    <div className="w-16 rounded-full h-[3px] bg-gray-700 mb-2" />
                                </div>

                                <div className="flex-1 overflow-y-scroll px-4 py-2 min-h-0">
                                    {SwitchAccountList?.sessions?.map((item) => (
                                        <button
                                            type="button"
                                            onClick={() => handleSwitchAccount(item.access_token)}
                                            key={item?.id}
                                            className="w-full py-3 flex items-center justify-between gap-2"
                                        >
                                            <div className="flex items-center gap-1.5 min-w-0">
                                                <img
                                                    src={item?.avatar}
                                                    alt={item?.username}
                                                    className="h-10 w-10 rounded-full object-cover border border-gray-400 dark:border-gray-800"
                                                />
                                                <p className="text-[13px] font-semibold text-black dark:text-white text-left line-clamp-1">
                                                    {item?.username}
                                                </p>
                                            </div>
                                            {isCurrentActiveAccount(item) && (
                                                <IoCheckmarkCircle className="text-[18px] text-blue-500 shrink-0" />
                                            )}
                                        </button>
                                    ))}

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSwitchTab(false);
                                            navigate("/switch/account");
                                        }}
                                        className="w-full py-3 flex items-center gap-3"
                                    >
                                        <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 flex items-center justify-center text-[26px] leading-none">
                                            +
                                        </div>
                                        <p className="text-[13px] font-semibold text-black dark:text-white">
                                            Add New Account
                                        </p>
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>,
                portalTarget
            )}
        </div >
    );
});

export default AccountProfileSection;